/**
 * 修正: 同梱不可商品と通常商品の同時カートインを防止
 *
 * - 商品タグ「同梱不可」の商品は line item properties の `_is_exclusive` で判定
 * - 同梱不可商品どうしは同時購入を許可
 * - 同梱不可商品と通常商品の混在のみブロック
 */

const EXCLUSIVE_PROPERTY_KEY = "_is_exclusive";
const EXCLUSIVE_BLOCKED_MESSAGE =
  "同梱不可商品とその他商品は一緒に購入できません。別々でご購入いただきますようお願いいたします。";

type CartItemProperties = Record<string, string | number | boolean | null>;

interface CartItem {
  properties?: CartItemProperties | null;
}

interface CartResponse {
  items: CartItem[];
}

let isExclusiveCartInitialized = false;

const hasExclusiveProperty = (
  properties?: CartItemProperties | null,
): boolean => {
  if (!properties) {
    return false;
  }

  return properties[EXCLUSIVE_PROPERTY_KEY] === "1";
};

const hasExclusiveInCart = (items: CartItem[]): boolean =>
  items.some((item) => hasExclusiveProperty(item.properties));

const hasNormalInCart = (items: CartItem[]): boolean =>
  items.some((item) => !hasExclusiveProperty(item.properties));

const isExclusiveProduct = (form: HTMLFormElement): boolean => {
  const input = form.querySelector<HTMLInputElement>(
    `input[name="properties[${EXCLUSIVE_PROPERTY_KEY}]"]`,
  );

  return input?.value === "1";
};

const fetchCart = async (): Promise<CartResponse> => {
  const response = await fetch("/cart.js", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("カート情報の取得に失敗しました");
  }

  return response.json();
};

const showError = (form: HTMLFormElement, message: string): void => {
  const productForm = form.closest("product-form");
  if (productForm) {
    const errorWrapper = productForm.querySelector<HTMLElement>(
      ".product-form__error-message-wrapper",
    );
    const errorMessage = productForm.querySelector<HTMLElement>(
      ".product-form__error-message",
    );

    if (errorWrapper && errorMessage) {
      errorMessage.textContent = message;
      errorWrapper.removeAttribute("hidden");

      window.setTimeout(() => {
        errorWrapper.setAttribute("hidden", "");
      }, 5000);
      return;
    }
  }

  alert(message);
};

const triggerOriginalSubmit = (form: HTMLFormElement): void => {
  // 修正: 再送信時だけガードを外して元の product-form submit を通す
  form.dataset.exclusiveCartBypass = "1";

  const submitButton = form.querySelector<HTMLButtonElement>("[type='submit']");
  if (submitButton) {
    form.requestSubmit(submitButton);
    return;
  }

  form.requestSubmit();
};

const isAddToCartForm = (target: EventTarget | null): target is HTMLFormElement =>
  target instanceof HTMLFormElement && target.dataset.type === "add-to-cart-form";

const initExclusiveCart = (): void => {
  if (isExclusiveCartInitialized) {
    return;
  }

  isExclusiveCartInitialized = true;

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!isAddToCartForm(form)) {
        return;
      }

      if (form.dataset.exclusiveCartBypass === "1") {
        delete form.dataset.exclusiveCartBypass;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      void (async () => {
        try {
          const cart = await fetchCart();
          if (cart.items.length === 0) {
            triggerOriginalSubmit(form);
            return;
          }

          const addingExclusive = isExclusiveProduct(form);
          const cartHasExclusive = hasExclusiveInCart(cart.items);
          const cartHasNormal = hasNormalInCart(cart.items);
          const shouldBlock =
            (addingExclusive && cartHasNormal) ||
            (!addingExclusive && cartHasExclusive);

          if (shouldBlock) {
            showError(form, EXCLUSIVE_BLOCKED_MESSAGE);
            return;
          }

          triggerOriginalSubmit(form);
        } catch (error) {
          console.error("同梱不可商品チェックエラー:", error);
          triggerOriginalSubmit(form);
        }
      })();
    },
    true,
  );
};

export { initExclusiveCart };
